import type React from "react";
import { useRef, useState } from "react";

export interface EditableFileOps {
  filesToRemove: number[];
  filesToReplace: Array<{ fileId: number; newFile: File }>;
  filesToAdd: File[];
}

export const useEditableAttachments = () => {
  const [filesToRemove, setFilesToRemove] = useState<Set<number>>(new Set());
  const [filesToReplace, setFilesToReplace] = useState<Map<number, File>>(
    new Map(),
  );
  const [filesToAdd, setFilesToAdd] = useState<File[]>([]);
  const addFilesInputRef = useRef<HTMLInputElement>(null);

  const removeExistingFile = (fileId: number) => {
    setFilesToRemove((previous) => {
      const next = new Set(previous);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
    setFilesToReplace((previous) => {
      if (!previous.has(fileId)) return previous;
      const next = new Map(previous);
      next.delete(fileId);
      return next;
    });
  };

  const replaceExistingFile = (fileId: number, newFile: File) => {
    setFilesToReplace((previous) => new Map(previous).set(fileId, newFile));
    setFilesToRemove((previous) => {
      if (!previous.has(fileId)) return previous;
      const next = new Set(previous);
      next.delete(fileId);
      return next;
    });
  };

  const removeNewFile = (index: number) => {
    setFilesToAdd((previous) =>
      previous.filter((_, itemIndex) => itemIndex !== index),
    );
  };

  const addFilesInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files ?? []);
    if (selected.length > 0) {
      setFilesToAdd((previous) => [...previous, ...selected]);
    }
    event.target.value = "";
  };

  const reset = () => {
    setFilesToRemove(new Set());
    setFilesToReplace(new Map());
    setFilesToAdd([]);
  };

  const fileOps: EditableFileOps = {
    filesToRemove: [...filesToRemove],
    filesToReplace: [...filesToReplace.entries()].map(([fileId, newFile]) => ({
      fileId,
      newFile,
    })),
    filesToAdd,
  };

  return {
    filesToRemove,
    filesToReplace,
    filesToAdd,
    addFilesInputRef,
    fileOps,
    hasFileChanges:
      filesToRemove.size > 0 ||
      filesToReplace.size > 0 ||
      filesToAdd.length > 0,
    removeExistingFile,
    replaceExistingFile,
    removeNewFile,
    addFilesInputChange,
    reset,
  };
};
